import * as React from 'react'
import { ClientSafeProvider, LiteralUnion } from 'next-auth/react'
import { getProviders } from 'next-auth/react'
import useSWR from 'swr'

import { BuiltInProviderType } from 'next-auth/providers'
import useAuth from 'components/hooks/use-auth'
import devvSdk from 'utils/devv-sdk'

/** User */

export function useUser() {
    const auth = useAuth()

    const fetcher = () => devvSdk.getUserById(auth.userId as string).then((res) => res.data)

    const _shouldFetch = auth.status === 'authenticated'

    const { data, error, mutate } = useSWR(_shouldFetch ? `user` : null, fetcher)

    const loading = !data && !error && _shouldFetch

    return { user: data, loading, error, mutate }
}

/** Auth */

export function useAuthProviders() {
    const [providers, setProviders] = React.useState<
        Record<LiteralUnion<BuiltInProviderType, string>, ClientSafeProvider> | undefined | null
    >(undefined)
    const [loading, setLoading] = React.useState(false)

    const fetchProviders = async () => {
        setLoading(true)
        getProviders()
            .then((res) => {
                setLoading(false)
                setProviders(res)
            })
            .catch(() => {
                setLoading(false)
            })
    }

    React.useEffect(() => {
        fetchProviders()
    }, [])

    return { providers, loading }
}

/** Billing */

export function useStripePrices() {
    const fetcher = () => devvSdk.getStripePrices().then((res) => res.data)

    return useSWR(`prices`, fetcher)
}

/** Path */

export function usePath({ pathId, shouldFetch = true }: { pathId: string; shouldFetch?: boolean }) {
    const fetcher = () => devvSdk.getPathById(pathId).then((res) => res.data)

    const { data, error } = useSWR(shouldFetch ? `paths/${pathId}` : null, fetcher)

    const loading = !data && !error && shouldFetch

    return { data, loading, error }
}

export function usePathProgress({ pathId, shouldFetch = true }: { pathId: string; shouldFetch?: boolean }) {
    const fetcher = () => devvSdk.getPathProgress(pathId).then((res) => res.data)

    const { user, loading: userLoading } = useUser()

    const _shouldFetch = shouldFetch && !!user?.enrolled_paths.includes(pathId)

    const { data, error } = useSWR(_shouldFetch ? `paths/${pathId}/progress` : null, fetcher)

    const loading = (!data && !error && _shouldFetch) || userLoading

    return { data, loading, error }
}

export function usePathsProgress() {
    const fetcher = () => devvSdk.getPathsProgress().then((res) => res.data)

    const auth = useAuth()

    const shouldFetch = auth.status === 'authenticated'

    const { data, error } = useSWR(shouldFetch ? `paths/progress` : null, fetcher)

    const loading = !data && !error && shouldFetch

    return { data, loading, error }
}

/** Courses & Tracks */

export function useCourse({ courseId, shouldFetch = true }: { courseId: string; shouldFetch?: boolean }) {
    const fetcher = () => devvSdk.getCourseById(courseId).then((res) => res.data)

    const { data, error } = useSWR(shouldFetch ? `courses/${courseId}` : null, fetcher)

    const loading = !data && !error && shouldFetch

    return { data, loading, error }
}

export function useCourseProgress({ courseId, shouldFetch = true }: { courseId: string; shouldFetch?: boolean }) {
    const fetcher = () => devvSdk.getCourseProgress(courseId).then((res) => res.data)

    const { user, loading: userLoading } = useUser()

    const _shouldFetch = shouldFetch && !!user?.enrolled_tracks?.includes(courseId)

    const { data, error } = useSWR(_shouldFetch ? `courses/${courseId}/progress` : null, fetcher)

    const loading = (!data && !error && _shouldFetch) || userLoading

    return { data, loading, error }
}

export function useCoursesProgress() {
    const fetcher = () => devvSdk.getCoursesProgress().then((res) => res.data)

    const auth = useAuth()

    const shouldFetch = auth?.status === 'authenticated'

    const { data, error } = useSWR(shouldFetch ? `courses/progress` : null, fetcher)

    const loading = !data && !error && shouldFetch

    return { data, loading, error }
}

/** Resources */

export function useNextResource(props: {
    itemType: 'paths' | 'courses'
    itemId: string
    currentResourceId: string
    shouldFetch?: boolean
}) {
    const { itemId, itemType, currentResourceId, shouldFetch = true } = props

    const fetcher = () =>
        itemType === 'paths'
            ? devvSdk.getPathNextResource({ pathId: itemId, currentResourceId }).then((res) => res.data)
            : devvSdk.getCourseNextResource({ courseId: itemId, currentResourceId }).then((res) => res.data)

    const _shouldFetch = shouldFetch && !!itemId

    const { data, error, mutate } = useSWR(
        _shouldFetch ? `${itemType}/${itemId}/resource-after/${currentResourceId}` : null,
        fetcher,
    )

    const loading = !data && !error && _shouldFetch

    return { data, loading, error, mutate }
}

export function useResourcesOverview(props: { resources: string[]; shouldFetch?: boolean }) {
    const { resources, shouldFetch } = props

    const fetcher = () => devvSdk.getResourcesOverview(resources).then((res) => res.data)

    const { data, error } = useSWR(
        shouldFetch ? `resources/overview?ids=[${JSON.stringify(resources)}]` : null,
        fetcher,
    )

    const loading = !data && !error && shouldFetch

    return { data, loading, error }
}

export function useResourceOverview(props: { resourceId?: string; shouldFetch?: boolean }) {
    const { resourceId, shouldFetch } = props

    const fetcher = () => devvSdk.getResourcesOverview([resourceId || '']).then((res) => res.data[0])

    const { data, error } = useSWR(shouldFetch ? `resources/overview?ids=[${resourceId}]` : null, fetcher)

    const loading = !data && !error && shouldFetch

    return { data, loading, error }
}

export function useCanUserAccessResource(resourceId: string) {
    const fetcher = () => devvSdk.canUserAccessResource({ resourceId }).then((res) => res.data)

    const auth = useAuth()

    const _shouldFetch = auth.status === 'authenticated'

    const { data, error } = useSWR(_shouldFetch ? `users/can-access/${resourceId}` : null, fetcher)

    const loading = (!data && !error && _shouldFetch) || auth.status === 'loading'

    return { canAccess: data?.can_access, loading, error }
}

export function useLessonProgress({ lessonId, shouldFetch = true }: { lessonId?: string; shouldFetch?: boolean }) {
    const fetcher = () => devvSdk.getLessonProgress(lessonId as string).then((res) => res.data)

    const auth = useAuth()

    const _shouldFetch = shouldFetch && !!lessonId && auth.status === 'authenticated'

    const { data, error, mutate } = useSWR(_shouldFetch ? `resources/lessons/${lessonId}/progress` : null, fetcher)

    const loading = (!data && !error && _shouldFetch) || auth.status === 'loading'

    return { data, loading, error, mutate }
}

export function useExerciseProgress({
    exerciseId,
    shouldFetch = true,
}: {
    exerciseId?: string
    shouldFetch?: boolean
}) {
    const fetcher = () => devvSdk.getExerciseProgress(exerciseId as string).then((res) => res.data)

    const auth = useAuth()

    const _shouldFetch = shouldFetch && !!exerciseId && auth?.status === 'authenticated'

    const { data, error, mutate } = useSWR(_shouldFetch ? `exercises/${exerciseId}/progress` : null, fetcher)

    const loading = (!data && !error && _shouldFetch) || auth?.status === 'loading'

    return { data, loading, error, mutate }
}

export function useProjectProgress({ projectId, shouldFetch = true }: { projectId?: string; shouldFetch?: boolean }) {
    const fetcher = () => devvSdk.getProjectProgress(projectId as string).then((res) => res.data)

    const auth = useAuth()

    const _shouldFetch = shouldFetch && !!projectId && auth?.status === 'authenticated'

    const { data, error, mutate } = useSWR(_shouldFetch ? `projects/${projectId}/progress` : null, fetcher)

    const loading = (!data && !error && _shouldFetch) || auth?.status === 'loading'

    return { data, loading, error, mutate }
}

/** Other */
